import { nameUnit } from '@/validations/validatorUnits'
import { required, maxLength, minLength } from '@vuelidate/validators'
import { isValidEmailAddress } from '@/validations/validators'
import {
  getHasBlockNumbers,
  getHasFreeSingleNumbers,
  getIsNumberModeBlock,
  getIsValidDirectDial,
  getIsValidQuickDial,
} from './validators'
import router from '@/router'
import type { Config } from './tour'
import { useDevStore } from '@/stores/dev/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useCtaStore } from '@/stores/cta/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { computed } from 'vue'
import { i18n } from '@/core/i18n'

export const CTABasicSetupConfig: Config = {
  onStart() {
    // Redirect to overview
    router.push('/').catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        throw error
      }
    })

    // Force re-render to reset the view (if in edit extensions mode, the add button is hidden)
    useDevStore().forceRerender()
  },
  steps: [
    // Show requirements
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.basicSetup.intro.title',
      text: 'tutorial.cta.basicSetup.intro.text',
      showNextButton: true,
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      canGoToNext() {
        // Check if needs to upgrade their plan

        const extensions = useExtensionsStore().list

        const devices = useDevicesStore().list

        const ctaData = useCtaStore().get

        const hasMaxExtensions = extensions.length >= (ctaData.maxLines ?? 0)

        const hasMaxDevices = devices.length >= (ctaData.maxDevices ?? 0)

        const needsToUpgradePlan = hasMaxExtensions || hasMaxDevices

        if (needsToUpgradePlan) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.maxLines.invalid'
              )
            ),
          })

          return false
        }

        // Check if has any usable numbers for the new extension

        const hasBlockNumbers = getHasBlockNumbers()

        const hasFreeSingleNumbers = getHasFreeSingleNumbers()

        if (!hasBlockNumbers && !hasFreeSingleNumbers) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.freeNumbers.invalid'
              )
            ),
          })

          return false
        }

        return true
      },
    },
    // Close the entra import suggestion popup (if visible)
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.basicSetup.entraImportSuggestion.title',
      text: 'tutorial.cta.basicSetup.entraImportSuggestion.text',
      showOn() {
        const element = document.querySelector<HTMLElement>(
          '[data-test=import-suggestion-modal]'
        )

        return element !== null
      },
      attachElement: '[data-test=import-suggestion-modal] [data-test=cancel]',
      nextOnClickElements: [
        '[data-test=import-suggestion-modal] [data-test=cancel]',
      ],
      attachPosition: 'top',
    },
    // Close the ConnectMe popup (if visible)
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.basicSetup.entraImportSuggestion.title',
      text: 'tutorial.cta.basicSetup.entraImportSuggestion.text',
      showOn() {
        const element = document.querySelector<HTMLElement>(
          '[data-test=connect-me-modal]'
        )

        return element !== null
      },
      attachElement:
        '[data-test=connect-me-modal] [data-test=modal-primary-button]',
      nextOnClickElements: [
        '[data-test=connect-me-modal] [data-test=modal-primary-button]',
      ],
      attachPosition: 'top',
    },
    // Create an extension: add extension
    {
      title: 'tutorial.cta.basicSetup.addExtension.title',
      text: 'tutorial.cta.basicSetup.addExtension.text',
      attachElement:
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      nextOnClickElements: [
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      ],
      attachPosition: 'top',
    },
    // Create an extension: set name
    {
      title: 'tutorial.cta.basicSetup.extension.name.title',
      text: 'tutorial.cta.basicSetup.extension.name.text',
      showNextButton: true,
      attachElement: '[data-test=extension-name]',
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extension-name]'],
      preventEnterAndTabInElements: ['[data-test=extension-name]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=extension-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        // Check if name already used

        const extensions = useExtensionsStore().list

        const extensionNames = extensions.map((extension) => {
          return extension.name
        })

        const isNameAlreadyUsed = extensionNames.some(
          (name) => name.toLowerCase() === value?.toLowerCase()
        )

        if (isNameAlreadyUsed) {
          return false
        }

        return true
      },
    },
    // Create an extension: set extension type
    {
      title: 'tutorial.cta.basicSetup.extension.type.title',
      text: 'tutorial.cta.basicSetup.extension.type.text',
      showNextButton: true,
      attachElement: '[data-test=extensionType]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extensionType]'],
      preventEnterAndTabInElements: ['[data-test=extensionType]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=extensionType]'
        )

        const value = element?.value

        // Check that extension type is set to "Call"
        return value === 'default'
      },
    },
    // Create an extension: set number mode
    {
      title: 'tutorial.cta.basicSetup.extension.numberMode.title',
      text: 'tutorial.cta.basicSetup.extension.numberMode.text',
      showNextButton: true,
      attachElement: '[data-test=numberMode]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=numberMode]'],
      preventEnterAndTabInElements: ['[data-test=numberMode]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=numberMode]'
        )

        const value = element?.value

        switch (value) {
          case 'false':
            return getHasBlockNumbers()

          case 'true':
            return getHasFreeSingleNumbers()
        }

        return false
      },
    },
    // Create an extension: set phone number
    {
      title: 'tutorial.cta.basicSetup.extension.phoneNumber.title',
      text: 'tutorial.cta.basicSetup.extension.phoneNumber.text',
      showNextButton: true,
      attachElement: '[data-test=phone-number]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=phone-number]'],
      preventEnterAndTabInElements: ['[data-test=phone-number]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set direct dial (only for Block numbers)
    {
      title: 'tutorial.cta.basicSetup.extension.directDial.title',
      text: 'tutorial.cta.basicSetup.extension.directDial.text',
      showNextButton: true,
      attachElement: '[data-test=dial-options]',
      attachPosition: 'top',
      showOn: getIsNumberModeBlock,
      watchValueOfElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      preventEnterAndTabInElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      canGoToNext() {
        const directDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=direct-dial]'
        )

        const directDial = directDialElement?.value

        const quickDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const quickDial = quickDialElement?.value

        const trunkSelector = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const trunkId = trunkSelector?.value

        const isDirectDialValid = getIsValidDirectDial(
          directDial ?? '',
          trunkId ?? ''
        )

        const isQuickDialValid = getIsValidQuickDial(quickDial ?? '')

        return isDirectDialValid && isQuickDialValid
      },
    },
    // Create an extension: set quick dial (only for single numbers)
    {
      title: 'tutorial.cta.basicSetup.extension.quickDial.title',
      text: 'tutorial.cta.basicSetup.extension.quickDial.text',
      showNextButton: true,
      attachElement: '[data-test=quick-dial]',
      attachPosition: 'top',
      showOn() {
        const isBlockMode = getIsNumberModeBlock()

        return !isBlockMode
      },
      watchValueOfElements: ['[data-test=quick-dial]'],
      preventEnterAndTabInElements: ['[data-test=quick-dial]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const value = element?.value

        return getIsValidQuickDial(value ?? '')
      },
    },
    // Create an extension: set contact info email
    {
      title: 'tutorial.cta.basicSetup.extension.contactEmail.title',
      text: 'tutorial.cta.basicSetup.extension.contactEmail.text',
      showNextButton: true,
      attachElement: '[data-test=extension-email]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extension-email]'],
      preventEnterAndTabInElements: ['[data-test=extension-email]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=extension-email]'
        )

        const value = element?.value

        if (!required.$validator(value, undefined, undefined)) {
          return false
        }

        if (!isValidEmailAddress(value ?? '')) {
          return false
        }

        return true
      },
    },
    // Create an extension: save changes
    {
      title: 'tutorial.cta.basicSetup.extension.save.title',
      text: 'tutorial.cta.basicSetup.extension.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Overview: show where extensions are viewed
    {
      title: 'tutorial.cta.basicSetup.extensionOverview.title',
      text: 'tutorial.cta.basicSetup.extensionOverview.text',
      showNextButton: true,
      attachElement: '[data-test=overview-section-extension]',
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      attachPosition: 'top',
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test=edit-night-switch]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
    },
    // Create a device: add device
    {
      title: 'tutorial.cta.basicSetup.addDevice.title',
      text: 'tutorial.cta.basicSetup.addDevice.text',
      attachElement:
        '[data-test=overview-section-device] [data-test=add-new-section-item]',
      attachPosition: 'top',
      nextOnClickElements: [
        '[data-test=overview-section-device] [data-test=add-new-section-item]',
      ],
    },
    // Create a device: set device name
    {
      title: 'tutorial.cta.basicSetup.device.name.title',
      text: 'tutorial.cta.basicSetup.device.name.text',
      showNextButton: true,
      attachElement: '[data-test=device-name]',
      attachPosition: 'top',
      waitForElements: ['[data-test=device-page][data-is-loading=false]'],
      watchValueOfElements: ['[data-test=device-name]'],
      preventEnterAndTabInElements: ['[data-test=device-name]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=device-name]'
        )

        const value = element?.value

        if (
          !required.$validator(value, undefined, undefined) ||
          !minLength(1).$validator(value, undefined, undefined) ||
          !maxLength(32).$validator(value, undefined, undefined)
        ) {
          return false
        }

        // Check if is duplicate

        const devices = useDevicesStore().list

        const allDeviceNames = devices.map((device) => device.name)

        const isDuplicateDeviceName = allDeviceNames.includes(value!)

        if (isDuplicateDeviceName) {
          return false
        }

        return true
      },
    },
    // Create a device: set device type
    {
      title: 'tutorial.cta.basicSetup.device.type.title',
      text: 'tutorial.cta.basicSetup.device.type.text',
      showNextButton: true,
      attachElement: '[data-test=device-type]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=device-type]'],
      preventEnterAndTabInElements: ['[data-test=device-type]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=device-type]'
        )

        const value = element?.value

        // Check that the device type is set to "VoIP Phone"
        return value === 'voip'
      },
    },
    // Create a device: set outgoing call trunk
    {
      title: 'tutorial.cta.basicSetup.device.outgoingTrunk.title',
      text: 'tutorial.cta.basicSetup.device.outgoingTrunk.text',
      showNextButton: true,
      attachElement: '[data-test=outgoing-trunk]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=outgoing-trunk]'],
      preventEnterAndTabInElements: ['[data-test=outgoing-trunk]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=outgoing-trunk]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create a device: set display name
    {
      title: 'tutorial.cta.basicSetup.device.displayName.title',
      text: 'tutorial.cta.basicSetup.device.displayName.text',
      showNextButton: true,
      attachElement: '#displayname',
      attachPosition: 'top',
      watchValueOfElements: ['#displayname'],
      preventEnterAndTabInElements: ['#displayname'],
      canGoToNext() {
        const element =
          document.querySelector<HTMLSelectElement>('#displayname')

        const value = element?.value

        if (
          !required.$validator(value, undefined, undefined) ||
          !maxLength(32).$validator(value, undefined, undefined)
        ) {
          return false
        }

        return true
      },
    },
    // Create a device: set internal CLIP
    {
      title: 'tutorial.cta.basicSetup.device.internalClip.title',
      text: 'tutorial.cta.basicSetup.device.internalClip.text',
      showNextButton: true,
      attachElement: '[data-test=internal-clip]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=internal-clip]'],
      preventEnterAndTabInElements: ['[data-test=internal-clip]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=internal-clip]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create a device: set external CLIP
    {
      title: 'tutorial.cta.basicSetup.device.externalClip.title',
      text: 'tutorial.cta.basicSetup.device.externalClip.text',
      showNextButton: true,
      attachElement: '[data-test=clipType]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=clipType]'],
      preventEnterAndTabInElements: ['[data-test=clipType]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=clipType]'
        )

        const value = element?.value

        if (!required.$validator(value, undefined, undefined)) {
          return false
        }

        // Any option is valid, except 'custom'
        if (value === 'custom') {
          return false
        }

        return true
      },
    },
    // Create a device: assign extensions: open assign extensions page
    {
      title: 'tutorial.cta.basicSetup.device.openAssignExtension.title',
      text: 'tutorial.cta.basicSetup.device.openAssignExtension.text',
      attachElement: '[data-test=assign-extensions]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=assign-extensions]'],
    },
    // Create a device: assign extensions: select extension
    {
      delay: 500, // Wait for AssignExtensions to finish it's scroll
      title:
        'tutorial.cta.basicSetup.device.assignExtension.selectExtension.title',
      text: 'tutorial.cta.basicSetup.device.assignExtension.selectExtension.text',
      attachElement: '[data-test=available-entities]',
      attachPosition: 'bottom',
      nextOnClickElements: [
        '[data-test=available-entities] [data-test=entity]',
      ],
    },
    // Create a device: assign extensions: save changes
    {
      title: 'tutorial.cta.basicSetup.device.assignExtension.save.title',
      text: 'tutorial.cta.basicSetup.device.assignExtension.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Create a device: generate device config
    {
      title: 'tutorial.cta.basicSetup.device.generateConfig.title',
      text: 'tutorial.cta.basicSetup.device.generateConfig.text',
      attachElement: '#save-generate-login-info',
      attachPosition: 'top',
      nextOnClickElements: ['#save-generate-login-info'],
      waitForElements: ['[data-test=device-page][data-is-loading=false]'],
    },
    // Create a device: show device config to the user
    {
      title: 'tutorial.cta.basicSetup.device.regidterDevice.title',
      text: 'tutorial.cta.basicSetup.device.regidterDevice.text',
      showNextButton: true,
      attachElement: '[data-test=configure-device]',
      attachPosition: 'top',
      waitForElements: [
        '[data-test=provisioning-select]',
        '[data-test=device-page][data-is-loading=false]',
      ],
    },
    // Create a device: save changes
    {
      title: 'tutorial.cta.basicSetup.device.save.title',
      text: 'tutorial.cta.basicSetup.device.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Overview: show where the device info is viewed
    {
      title: 'tutorial.cta.basicSetup.deviceOverview.title',
      text: 'tutorial.cta.basicSetup.deviceOverview.text',
      showFinishButton: true,
      attachElement: '[data-test=overview-section-device]',
      attachPosition: 'top',
      waitForElements: [
        '[data-test=overview-section-device] [data-test=section-container][data-loading=false]',
      ],
    },
  ],
}
